import React, { useState, useRef, useEffect } from 'react';
import {
    GoogleMap,
    LoadScript,
    Marker,
    DrawingManager,
    Polygon
} from '@react-google-maps/api';

const containerStyle = {
    width: '100%',
    height: '600px'
};

// Valores predeterminados para el centro del mapa
const defaultCenter = {
    lat: 40.416775, // Madrid, por ejemplo
    lng: -3.703790
};

// Opciones para el administrador de dibujo
const drawingManagerOptions = {
    drawingControl: true,
    drawingControlOptions: {
        position: window.google?.maps?.ControlPosition?.TOP_CENTER,
        drawingModes: [
            window.google?.maps?.drawing?.OverlayType?.POLYGON,
        ]
    },
    polygonOptions: {
        fillColor: '#2196F3',
        fillOpacity: 0.3,
        strokeWeight: 2,
        strokeColor: '#1976D2',
        editable: true,
        draggable: true,
    }
};

const HouseCounterMap = () => {
    const [map, setMap] = useState(null);
    const [center, setCenter] = useState(defaultCenter);
    const [polygons, setPolygons] = useState([]);
    const [buildings, setBuildings] = useState([]);
    const [buildingCounts, setBuildingCounts] = useState({});
    const [locationLoading, setLocationLoading] = useState(true);
    const drawingManagerRef = useRef(null);

    // Esta función sería llamada cuando se complete un polígono mediante el dibujo
    const onPolygonComplete = polygon => {
        const polygonPath = polygon.getPath().getArray().map(latLng => ({
            lat: latLng.lat(),
            lng: latLng.lng()
        }));

        const newPolygonId = Date.now().toString();
        const newPolygon = {
            id: newPolygonId,
            path: polygonPath,
            ref: polygon
        };

        setPolygons(prev => [...prev, newPolygon]);

        // Escuchar los cambios en el polígono (cuando se edita o se mueve)
        polygon.addListener('mouseup', () => {
            setPolygons(prev => {
                return prev.map(p => {
                    if (p.id === newPolygonId) {
                        const updatedPath = polygon.getPath().getArray().map(latLng => ({
                            lat: latLng.lat(),
                            lng: latLng.lng()
                        }));
                        return { ...p, path: updatedPath };
                    }
                    return p;
                });
            });
        });

        // Reiniciar el modo de dibujo
        if (drawingManagerRef.current) {
            drawingManagerRef.current.setDrawingMode(null);
        }
    };

    // Función para obtener edificios en el área visible del mapa
    const fetchBuildingsInView = () => {
        if (!map) return;

        // En un entorno de producción, aquí conectaríamos con una API real
        // que nos permita identificar edificios residenciales.
        // Posibles fuentes:
        // 1. OpenStreetMap API - tiene mejor cobertura de edificios residenciales
        // 2. Servicios especializados de reconocimiento de imágenes satelitales

        // Por ahora, simplemente vamos a limpiar los edificios simulados
        // ya que no representan casas reales en el mapa
        setBuildings([]);

        // Aquí iría la lógica para obtener datos reales
    };

    // Actualizar los conteos de edificios cuando cambien los polígonos o los edificios
    useEffect(() => {
        const counts = {};

        polygons.forEach(polygon => {
            const buildingsInPolygon = buildings.filter(building =>
                isPointInPolygon(building.position, polygon.path)
            );
            counts[polygon.id] = buildingsInPolygon.length;
        });

        setBuildingCounts(counts);
    }, [polygons, buildings]);

    // Función para comprobar si un punto está dentro de un polígono
    const isPointInPolygon = (point, polygonPath) => {
        // Implementación del algoritmo "ray casting"
        let inside = false;
        for (let i = 0, j = polygonPath.length - 1; i < polygonPath.length; j = i++) {
            const xi = polygonPath[i].lat;
            const yi = polygonPath[i].lng;
            const xj = polygonPath[j].lat;
            const yj = polygonPath[j].lng;

            const intersect = ((yi > point.lng) !== (yj > point.lng))
                && (point.lat < (xj - xi) * (point.lng - yi) / (yj - yi) + xi);
            if (intersect) inside = !inside;
        }

        return inside;
    };

    // Función para borrar todos los polígonos
    const clearAllPolygons = () => {
        polygons.forEach(polygon => {
            if (polygon.ref) {
                polygon.ref.setMap(null);
            }
        });
        setPolygons([]);
        setBuildingCounts({});
    };

    // Obtener la ubicación del usuario cuando se carga el componente
    useEffect(() => {
        if (navigator.geolocation) {
            setLocationLoading(true);
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setCenter({
                        lat: position.coords.latitude,
                        lng: position.coords.longitude
                    });
                    setLocationLoading(false);
                },
                (error) => {
                    console.error("Error obteniendo la ubicación:", error);
                    setLocationLoading(false);
                    // Mantener la ubicación predeterminada si hay un error
                }
            );
        } else {
            setLocationLoading(false);
            console.log("La geolocalización no está soportada por este navegador.");
        }
    }, []);

    const onMapLoad = (mapInstance) => {
        setMap(mapInstance);
    };

    const onBoundsChanged = () => {
        fetchBuildingsInView();
    };

    const onDrawingManagerLoad = (drawingManager) => {
        drawingManagerRef.current = drawingManager;
    };

    return (
        <div className="h-screen w-full flex flex-col">
            <div className="flex justify-between p-4 bg-gray-100">
                <h1 className="text-xl font-bold">Contador de Casas con Google Maps</h1>
                <div className="flex items-center space-x-3">
                    {locationLoading && (
                        <span className="text-blue-600">Obteniendo tu ubicación...</span>
                    )}
                    <button
                        onClick={clearAllPolygons}
                        className="px-4 py-2 bg-red-500 text-white rounded"
                    >
                        Limpiar polígonos
                    </button>
                </div>
            </div>

            <div className="flex-1">
                <LoadScript
                    googleMapsApiKey="AIzaSyBSXE8zZ-SqMWdDz16hr00Djvkm3mMit28"
                    libraries={['drawing', 'places']}
                >
                    <GoogleMap
                        mapContainerStyle={containerStyle}
                        center={center}
                        zoom={16}
                        onLoad={onMapLoad}
                        onBoundsChanged={onBoundsChanged}
                        options={{
                            mapTypeControl: true,
                            streetViewControl: true,
                            fullscreenControl: true
                        }}
                    >
                        {/* Administrador de dibujo para crear polígonos */}
                        {map && (
                            <DrawingManager
                                onLoad={onDrawingManagerLoad}
                                options={drawingManagerOptions}
                                onPolygonComplete={onPolygonComplete}
                            />
                        )}

                        {/* Renderizar edificios con un punto encima de cada casa */}
                        {buildings.map(building => (
                            <Marker
                                key={building.id}
                                position={building.position}
                                icon={{
                                    path: window.google?.maps?.SymbolPath?.CIRCLE,
                                    scale: building.type === 'house' ? 6 : 4,
                                    fillColor: building.type === 'house' ? '#FF9800' : '#4CAF50',
                                    fillOpacity: 1,
                                    strokeWeight: 1,
                                    strokeColor: '#FFFFFF'
                                }}
                                title={`${building.type === 'house' ? 'Casa' : 'Edificio'} de ${building.floors} piso(s)`}
                            />
                        ))}

                        {/* Punto encima de cada casa (especial) */}
                        {buildings.filter(b => b.type === 'house').map(house => (
                            <Marker
                                key={`dot-${house.id}`}
                                position={{
                                    lat: house.position.lat,
                                    lng: house.position.lng
                                }}
                                icon={{
                                    path: window.google?.maps?.SymbolPath?.CIRCLE,
                                    scale: 2,
                                    fillColor: '#FF0000',
                                    fillOpacity: 1,
                                    strokeWeight: 0
                                }}
                                zIndex={2}
                            />
                        ))}

                        {/* Renderizar polígonos y conteos */}
                        {polygons.map(polygon => (
                            <React.Fragment key={polygon.id}>
                                {/* No necesitamos renderizar el polígono aquí porque ya está en el mapa */}

                                {/* Etiqueta con el conteo */}
                                {buildingCounts[polygon.id] !== undefined && (
                                    <Marker
                                        position={getCenterOfPolygon(polygon.path)}
                                        label={{
                                            text: `${buildingCounts[polygon.id]}`,
                                            color: 'white',
                                            fontWeight: 'bold'
                                        }}
                                        icon={{
                                            path: window.google?.maps?.SymbolPath?.CIRCLE,
                                            scale: 15,
                                            fillColor: '#1976D2',
                                            fillOpacity: 0.9,
                                            strokeWeight: 0
                                        }}
                                    />
                                )}
                            </React.Fragment>
                        ))}
                    </GoogleMap>
                </LoadScript>
            </div>

            <div className="p-4 bg-gray-100">
                <h2 className="text-lg font-semibold mb-2">Notas importantes:</h2>
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                    <p className="text-yellow-700">
                        <strong>Limitación técnica:</strong> La API de Google Maps no proporciona directamente una forma de identificar y contar casas residenciales.
                        Para implementar esta funcionalidad en producción, se necesitaría:
                    </p>
                    <ul className="list-disc pl-5 mt-2 text-yellow-700">
                        <li>Utilizar la API de OpenStreetMap que tiene mejor cobertura de edificios residenciales</li>
                        <li>Usar servicios de procesamiento de imágenes satelitales como Google Earth Engine, Planet o Mapbox</li>
                        <li>Implementar un algoritmo de reconocimiento de imágenes para detectar estructuras residenciales</li>
                    </ul>
                </div>
                <h2 className="text-lg font-semibold mb-2">Instrucciones:</h2>
                <ol className="list-decimal pl-5">
                    <li>El mapa se centra automáticamente en tu ubicación actual</li>
                    <li>Usa el control de dibujo para crear polígonos en el mapa</li>
                    <li>Una vez implementada la detección de casas, podrás contarlas dentro de tus polígonos</li>
                    <li>Puedes editar o mover los polígonos después de crearlos</li>
                </ol>
            </div>
        </div>
    );
};

// Función auxiliar para calcular el centro de un polígono
function getCenterOfPolygon(path) {
    if (!path || path.length === 0) return { lat: 0, lng: 0 };

    let sumLat = 0;
    let sumLng = 0;

    path.forEach(point => {
        sumLat += point.lat;
        sumLng += point.lng;
    });

    return {
        lat: sumLat / path.length,
        lng: sumLng / path.length
    };
}

export default HouseCounterMap;